import React, { useEffect, useMemo, useRef, useState } from 'react';
import { find, isEmpty, map, size, some, cloneDeep, isFunction } from 'lodash';
import i18n from 'i18n';
import { FocusableParent } from 'components/navigation';
import useOnBackClickHandler from 'hooks/useOnBackClickHandler';
import ArrowIcon from 'components/Icons/back';
import CheckedIcon from 'components/Icons/checked';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import { setIsMenuVisible } from 'actions/menu';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsMenuVisible } from 'reducers/menu/selectors';
import getLocalized from 'utils/get-localized';
import { useHistory } from 'react-router-dom';
import {
  StyledBackdrop,
  StyledPanel,
  StyledTitle,
  StyledList,
  StyledRow,
  StyledFilterName,
  StyledFilterValues,
  StyledListItem,
  StyledClearButtom,
  StyledLeftBarIcon,
} from './styled';

const FILTERS_SECTION_NAME = 'FILTERS';
const OPTIONS_SECTION_NAME = 'FILTER_OPTIONS';
const LEFT_BAR_SECTION_NAME = 'LEFT_BAR';

const FilterView = ({ filters, activeFilters, defaultFocusIndex, onEnter, onItemFocus, onClear }) => {
  const containerRef = useRef();
  const [offset, setOffset] = useState(0);
  const startScrollIndex = 3;

  const config = {
    leaveFor: {
      up: '',
      down: '',
    },
  };

  const showClear = !isEmpty(activeFilters);

  const onFocus = (index) => {
    const size = Object.keys(filters).length;
    const i = Math.min(Math.max(index - startScrollIndex, 0), size - 1 - startScrollIndex * 2);
    setOffset(i * 100);
    if (isFunction(onItemFocus)) onItemFocus(index);
  };

  useEffect(() => {
    // Set default focus after the mount
    setTimeout(() => {
      const node = containerRef.current?.children?.[defaultFocusIndex];
      if (node) SpatialNavigationWrapper.focus(node);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledPanel sectionName={FILTERS_SECTION_NAME} config={config} defaultSection>
      <StyledTitle>{i18n.t('filtersPage.title')}</StyledTitle>
      <StyledList>
        <div ref={containerRef} style={{ transform: `translateY(${-offset}px)` }}>
          {Object.keys(filters).map((key, i) => {
            const selected = map(activeFilters[key], (option) => getLocalized(option.titles, option.title));
            const selectedLabel = size(selected) <= 1 ? selected.join(', ') : `${selected[0]}, +${size(selected) - 1}`;
            const isSorting = key === 'SORTING_UI';
            let name = i18n.t(`filtersPage.filters.${key}`);

            if (isSorting && !isEmpty(selected)) name = selectedLabel;

            return (
              <StyledListItem key={i} index={i} payload={key} onFocus={onFocus} onEnter={onEnter}>
                <StyledRow>
                  <StyledFilterName>{name}</StyledFilterName>
                  {!isSorting && <StyledFilterValues>{selectedLabel}</StyledFilterValues>}
                </StyledRow>
              </StyledListItem>
            );
          })}
        </div>
      </StyledList>
      <StyledClearButtom disabled={!showClear} onEnter={onClear}>
        {i18n.t('filtersPage.clear')}
      </StyledClearButtom>
    </StyledPanel>
  );
};

const OptionsView = ({ title, selectedFilter, selectedOptions, activeOptions, onEnter }) => {
  const [offset, setOffset] = useState(0);
  const startScrollIndex = 3;

  const config = {
    leaveFor: {
      up: '',
      down: '',
      left: '@LEFT_BAR',
      right: '',
    },
  };

  // Sorting values according to already seelcted options
  // Selected should be in the beginning of the list
  const sortedValues = useMemo(() => {
    // No sorting for the sigle-select filters
    if (!selectedFilter.isMultiSelect) return selectedFilter.values;

    return [...selectedFilter.values].sort((a, b) => {
      const isASelected = some(activeOptions, ['id', a.id]);
      const isBSelected = some(activeOptions, ['id', b.id]);

      if (isASelected) return -1;
      if (isBSelected) return 1;

      return 0;
    });
  }, [selectedFilter, activeOptions]);

  const onFocus = (index) => {
    const size = selectedFilter.values.length;
    const i = Math.min(Math.max(index - startScrollIndex, 0), size - 1 - startScrollIndex * 2);
    setOffset(i > 0 ? i * 100 : 0);
  };

  return (
    <>
      <StyledBackdrop />
      <StyledPanel config={config} sectionName={OPTIONS_SECTION_NAME} defaultSection>
        <StyledTitle>{title}</StyledTitle>
        <StyledList>
          <div style={{ transform: `translateY(${-offset}px)` }}>
            {sortedValues.map((option, i) => {
              const isSelected = some(selectedOptions, ['id', option.id]);
              const title = getLocalized(option.titles, option.title);
              return (
                <StyledListItem
                  key={i}
                  index={i}
                  payload={option.id}
                  onFocus={onFocus}
                  onEnter={onEnter}
                  isActive={isSelected}
                >
                  <StyledRow>
                    <StyledFilterName>{title}</StyledFilterName>
                    {isSelected && (
                      <StyledFilterValues>
                        <CheckedIcon />
                      </StyledFilterValues>
                    )}
                  </StyledRow>
                </StyledListItem>
              );
            })}
          </div>
        </StyledList>
      </StyledPanel>
    </>
  );
};

export const FilterPanel = ({ filters, activeFilters, setActive, isFolded, onFocus }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isMenuVisible = useSelector(selectIsMenuVisible);
  const [selectedFilterName, setSelectedFilterName] = useState(null);
  const [defaultFilterIndex, setDefaultFilterIndex] = useState(0);
  const [internalFilterState, setInternalFilterState] = useState(() => cloneDeep(activeFilters));

  const onFilterFocus = (index) => setDefaultFilterIndex(index);

  const onFilterEnter = (key) => {
    setSelectedFilterName(key);
    dispatch(setIsMenuVisible(false));
  };

  const showLeftBar = !!selectedFilterName || isFolded;

  const onFilterOptionSelect = (id) => {
    const { isMultiSelect } = filters[selectedFilterName];
    const option = find(filters[selectedFilterName].values, ['id', id]);

    setInternalFilterState((state) => {
      const upd = { ...state };

      upd[selectedFilterName] = upd[selectedFilterName] ?? [];

      if (some(upd[selectedFilterName], ['id', option.id])) {
        upd[selectedFilterName] = upd[selectedFilterName].filter(({ id }) => id !== option.id);
        if (isEmpty(upd[selectedFilterName])) delete upd[selectedFilterName];
      } else if (isMultiSelect) {
        upd[selectedFilterName] = [...upd[selectedFilterName], option];
      } else {
        upd[selectedFilterName] = [option];
      }

      return upd;
    });
  };

  const onClear = () => {
    // reset both states
    setActive({});
    setInternalFilterState({});
    setTimeout(() => SpatialNavigationWrapper.focus(`@${FILTERS_SECTION_NAME}`));
  };

  const resetView = () => {
    setSelectedFilterName(null);
    dispatch(setIsMenuVisible(true));
    setActive(internalFilterState);
  };

  const onLeftBarEnter = () => {
    if (selectedFilterName) {
      setTimeout(resetView, 0);
    } else {
      setTimeout(onFocus, 0);
    }
  };

  const contentView = selectedFilterName ? (
    <OptionsView
      title={i18n.t(`filtersPage.filters.${selectedFilterName}`)}
      selectedFilter={filters[selectedFilterName]}
      // selectedOptions is an interbal state
      selectedOptions={internalFilterState[selectedFilterName]}
      // activeOptions are already applied filters
      activeOptions={activeFilters[selectedFilterName]}
      onEnter={onFilterOptionSelect}
    />
  ) : (
    <FilterView
      filters={filters}
      activeFilters={activeFilters}
      defaultFocusIndex={defaultFilterIndex}
      onEnter={onFilterEnter}
      onItemFocus={onFilterFocus}
      onClear={onClear}
    />
  );

  useOnBackClickHandler(() => {
    if (selectedFilterName) {
      resetView();
    } else if (isFolded) {
      SpatialNavigationWrapper.focus(`@${LEFT_BAR_SECTION_NAME}`);
    } else {
      history.goBack();
    }
  });

  return (
    <>
      {showLeftBar && (
        <FocusableParent sectionName={LEFT_BAR_SECTION_NAME} onFocus={onLeftBarEnter}>
          <StyledLeftBarIcon onEnter={onLeftBarEnter} isMenuVisible={isMenuVisible}>
            <ArrowIcon />
          </StyledLeftBarIcon>
        </FocusableParent>
      )}
      {!isFolded && contentView}
    </>
  );
};

export default FilterPanel;
